import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {CreateAnalysisParams} from 'analysis-tool/AnalysisDAO';
import {SeedDataConfigType} from 'analysis-tool/create-analysis-modal/SeedDataTypes';
import {assert} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {ProcedureType, StudyPhase, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from 'procedures/utils/ProcedureCapabilityManagementHelpers';
import {TimingEstimateResult} from 'studies/progress/useTimingEstimate';
import {NoMethodWarningBanner, NoProcedureWarningBanner} from 'studies/sidepanel/ModalWarningBanners';
import {getProceedTextForStudyPhase} from 'studies/StudyPhaseUtils';
import MultiBatchSelector from 'studies/typed/process-development/multi-batch-selector/MultiBatchSelector';
import {CREATE_ANALYSIS_NO_PERMISSION_MESSAGE} from 'studies/typed/utils/analysis/analysisUtils';
import {
  getProcedureRunConditionTextForProcedureType,
  getProcedureTypeForStudyType,
} from 'studies/TypedStudyUtils';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import {assertNever} from 'util/Types';

import {createAnalysisForStudy} from '../utils/analysis/createAnalysisForStudy';
import ProcessDevelopmentAnalysisModalBanner from '../utils/analysis/ProcessDevelopmentAnalysisModalBanner';
import {StudyCreateAnalysisModalProps, TypedStudyAdapter} from '../utils/TypedStudyTypes';
import {
  UseProcessDevelopmentStudyForAnalysis,
  UseProcessDevelopmentStudyForAnalysisVariables,
} from './__generated/UseProcessDevelopmentStudyForAnalysis';
import {isNumConditionReplicatesValid} from './procedureRunStudyValidationUtils';
import ProcessDevelopmentStudyDesignOverview from './ProcessDevelopmentStudyDesignOverview';
import ProcessDevelopmentStudyDesignTabContents from './ProcessDevelopmentStudyDesignTabContents';
import ProcessDevelopmentStudyExecutionOverview from './ProcessDevelopmentStudyExecutionOverview';
import {ProcessDevelopmentStudyMetadata} from './ProcessDevelopmentStudyMetadata';
import {ProcessDevelopmentStudyPlanEditor} from './ProcessDevelopmentStudyPlanEditor';
import useProcessDevelopmentStudyState, {
  ProcessDevelopmentStudyState,
} from './useProcessDevelopmentStudyState';

export enum StepKey {
  Metadata = 'Metadata',
  DataEntryMode = 'DataEntryMode',
  Experiment = 'Experiment',
}

const styles = StyleSheet.create({
  multiBatchSelectorContainer: {
    // The Designs call for a 24px total padding. The modal already includes 16px of padding.
    padding: 8,
  },
});

export const ProcessDevelopmentStudyAdapter: TypedStudyAdapter<
  StepKey,
  'processDevInformation',
  ProcessDevelopmentStudyState
> = {
  type: StudyType.PROCESS_DEVELOPMENT,
  experimentKey: 'processDevInformation',

  isMultiExperimentEnabled: false,

  isMultiBatchEnabled: true,

  isMultiConditionEnabled: true,

  isNotificationSchedulesEnabled: true,

  isUnitOperationInstanceLinkingEnabled: false,

  allowReturnToDesignPhase: false,

  allowRequireDesignApproval: false,

  renderMetadataSection(state: ProcessDevelopmentStudyState) {
    return <></>;
  },

  modalOptions(stepKey: StepKey, studyType: StudyType) {
    if (stepKey === StepKey.Experiment) {
      return {
        extraLarge: true,
        bodyPadded: false,
        fullHeight: true,
        footer: (
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={`Upon creation, tasks will be generated and you won't be able to edit ${getProcedureRunConditionTextForProcedureType(
              getProcedureTypeForStudyType(studyType),
              {plural: true}
            )}.`}
          />
        ),
      };
    }
    return {
      mediumLarge: true,
    };
  },

  getButtonsByStep(
    currentStep: StepKey,
    updateCurrentStep: (step: StepKey) => void,
    submissionDisabled: boolean,
    isStudyMetadataValid: boolean,
    state: ProcessDevelopmentStudyState
  ) {
    switch (currentStep) {
      case StepKey.Metadata:
        return [
          {
            cancel: true,
            disabled: false,
            text: 'Cancel',
            dataTestAttrs: {component: 'CreateStudyModal', key: ModalButtonKey.CANCEL},
          },
          {
            // procedureId is filled out in StepKey.Metadata.
            disabled: !isStudyMetadataValid || state.procedureId == null,
            text: 'Next',
            style: ButtonStyle.SECONDARY,
            onClick: () => {
              updateCurrentStep(StepKey.DataEntryMode);
            },
            dataTestAttrs: {component: 'CreateStudyModal', key: ModalButtonKey.NEXT},
          },
        ];
      case StepKey.DataEntryMode:
        return [
          {
            cancel: true,
            disabled: false,
            text: 'Cancel',
            dataTestAttrs: {component: 'CreateStudyModal', key: ModalButtonKey.CANCEL},
          },
          {
            disabled: false,
            submit: false,
            text: 'Back',
            style: ButtonStyle.TERTIARY_GRAY,
            onClick: () => {
              updateCurrentStep(StepKey.Metadata);
            },
            dataTestAttrs: {component: 'CreateStudyModal', key: ModalButtonKey.BACK},
          },
          {
            disabled: false,
            text: 'Next',
            style: ButtonStyle.SECONDARY,
            onClick: () => {
              updateCurrentStep(StepKey.Experiment);
            },
            dataTestAttrs: {component: 'CreateStudyModal', key: ModalButtonKey.NEXT},
          },
        ];
      case StepKey.Experiment:
        return [
          {
            cancel: true,
            disabled: false,
            text: 'Cancel',
            dataTestAttrs: {component: 'CreateStudyModal', key: ModalButtonKey.CANCEL},
          },
          {
            disabled: false,
            submit: false,
            text: 'Back',
            style: ButtonStyle.TERTIARY_GRAY,
            onClick: () => {
              updateCurrentStep(StepKey.DataEntryMode);
            },
            dataTestAttrs: {component: 'CreateStudyModal', key: ModalButtonKey.BACK},
          },
          {
            disabled:
              submissionDisabled ||
              !isNumConditionReplicatesValid(state.methodExecutionLimitState) ||
              (this.isMultiExperimentEnabled && state.experimentName == null),
            submit: true,
            text: getProceedTextForStudyPhase(StudyPhase.EXECUTION),
            style: ButtonStyle.SECONDARY,
            dataTestAttrs: {component: 'CreateStudyModal', key: ModalButtonKey.SUBMIT},
          },
        ];
      default:
        return null;
    }
  },

  getSteps() {
    return [
      {
        key: StepKey.Metadata,
        title: 'Specify metadata',
      },
      {
        key: StepKey.DataEntryMode,
        title: 'Select data entry mode',
      },
      {
        key: StepKey.Experiment,
        title: 'Design experiment',
      },
    ];
  },

  renderStep(
    key: StepKey,
    state: ProcessDevelopmentStudyState,
    estimate: TimingEstimateResult,
    onRequestCloseModal: () => void,
    studyType: StudyType
  ) {
    const procedureType = getProcedureTypeForStudyType(studyType);
    switch (key) {
      case StepKey.Metadata:
        return (
          <ProcessDevelopmentStudyMetadata
            procedureId={state.procedureId}
            procedureType={procedureType}
            setProcedureId={state.setProcedureId}
            onRequestCloseModal={onRequestCloseModal}
          />
        );
      case StepKey.DataEntryMode:
        return (
          <div className={css(styles.multiBatchSelectorContainer)}>
            <MultiBatchSelector
              dataEntryMode={state.dataEntryMode}
              onSelectDataEntryMode={state.setDataEntryMode}
            />
          </div>
        );
      case StepKey.Experiment:
        if (state.procedureId == null) {
          return <NoProcedureWarningBanner procedureType={procedureType} />;
        }
        if (state.procedure == null || state.context == null || state.conditions == null) {
          return <HorizontalThrobber />;
        }
        if (state.selectedMethodId == null) {
          return <NoMethodWarningBanner procedureType={procedureType} />;
        }
        return (
          <>
            <ProcessDevelopmentStudyPlanEditor
              conditions={state.conditions}
              setConditions={state.setConditions}
              experimentalInputs={state.experimentalInputs}
              setExperimentalInputs={state.setExperimentalInputs}
              context={state.context}
              procedureFlowchart={state.procedure.procedureFlowchart}
              procedureMethods={state.procedure.methods}
              selectedMethodId={state.selectedMethodId}
              setSelectedMethodId={state.setSelectedMethodId}
              methodExecutionLimitState={state.methodExecutionLimitState}
              estimate={estimate}
              procedureType={procedureType}
              experimentName={state.experimentName}
              setExperimentName={state.setExperimentName}
              showExperimentName={this.isMultiExperimentEnabled}
              hasExperimentNameTemplate={state.hasExperimentNameTemplate}
            />
          </>
        );
      default:
        assert(false, `Unexpected key: ${assertNever(key)}`);
    }
  },
  useTypedStudyState: useProcessDevelopmentStudyState,
  getPerformanceMetadata(state: ProcessDevelopmentStudyState) {
    return {dataEntryMode: state.dataEntryMode};
  },
  getAnalyticsMetadata(state: ProcessDevelopmentStudyState) {
    const numReplicatesPerCondition =
      state.conditions != null ? state.conditions.map((condition) => condition.numReplicates) : [];
    const numConditions = numReplicatesPerCondition.length;
    return {
      numConditions,
      numReplicates: numReplicatesPerCondition,
      procedureId: state.procedureId,
      selectedMethodId: state.selectedMethodId,
    };
  },
  creation: {
    async: true,
    experimentParamRequired: true,
  },
  design: {
    ui: {
      tab: ProcessDevelopmentStudyDesignTabContents,
    },
  },
  overview: {
    ui: {
      tab: null,
      design: ProcessDevelopmentStudyDesignOverview,
      execution: ProcessDevelopmentStudyExecutionOverview,
    },
  },
  review: {
    analysis: {
      disallowAdhocAnalysis: false,
      useCreateAnalysisModalProps: (studyId: string, actionOrigin: ActionOrigin) => {
        const result = useBenchlingQuery<
          UseProcessDevelopmentStudyForAnalysis,
          UseProcessDevelopmentStudyForAnalysisVariables
        >(USE_PROCESS_DEVELOPMENT_STUDY_FOR_ANALYSIS, {variables: {studyId}});
        if (!isFeatureEnabledForProcedureType(ProcedureType.RECIPE, ProceduresFeature.CREATE_ANALYSIS)) {
          return {
            noPropsReason: CREATE_ANALYSIS_NO_PERMISSION_MESSAGE,
          };
        }
        if (result.type === QueryResultType.PENDING) {
          return {noPropsReason: 'Loading...'};
        }
        if (result.type === QueryResultType.ERROR) {
          return {noPropsReason: 'An unexpected error occurred. Refresh the page and try again.'};
        }
        if (isInaccessible(result.data.study)) {
          return {noPropsReason: 'You do not have access to this study.'};
        }
        if (result.data.study.procedureRun == null || isInaccessible(result.data.study.procedureRun)) {
          return {noPropsReason: 'You do not have access to the procedure run data.'};
        }
        const study = result.data.study;
        const procedureRun = result.data.study.procedureRun;

        return {
          props: (): StudyCreateAnalysisModalProps => ({
            actionOrigin,
            onCreateAnalysis: (data: CreateAnalysisParams) =>
              createAnalysisForStudy(data, {actionOrigin, studyType: StudyType.PROCESS_DEVELOPMENT}),
            initialStudySearchItems: [{id: study.id, name: study.name, humanId: study.humanId}],
            disableEditStudies: true,
            title: 'Generate analysis',
            headerInlineBanner: <ProcessDevelopmentAnalysisModalBanner />,
            hideDataSharingWarningBanner: true,
            initialSeedDataConfig: {
              type: SeedDataConfigType.PROCEDURE_RUN,
              data: {
                procedureRunId: procedureRun.id,
              },
            },
          }),
        };
      },
    },
    llm: {
      enabled: false,
    },
  },
};

export const USE_PROCESS_DEVELOPMENT_STUDY_FOR_ANALYSIS = gql`
  query UseProcessDevelopmentStudyForAnalysis($studyId: StrictString!)
  @engComponent(name: STUDIES)
  @skipAudit {
    study(id: $studyId) {
      ... on StudyNode {
        id
        name
        humanId
        folder {
          id
        }
        procedureRun {
          ... on ProcedureRunNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;
